import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { useEffect, useState } from "react";

import { useUserSession } from "contexts/userSession";
import {
  generateApolloClientInstance,
  generateApolloClientInstanceWithPhoenixSocket,
  generatePhoenixSocket,
} from "settings/apollo";
import { useApolloClientStore } from "stores/useApolloClientStore";

import useWorkspace from "./useWorkspace";

// The socket reads the cached ID token synchronously on every reconnect, and the server closes it when the
// token it connected with expires. Polling keeps that cache fresh in tabs that make no HTTP requests; most
// calls are cache hits and only hit Auth0 when the cached token is about to expire.
export const ID_TOKEN_REFRESH_INTERVAL_MS = 60_000;

export const useApolloClientManage = (): ApolloClient<NormalizedCacheObject> => {
  const { getIdToken, getCachedIdToken, redirectToLogin, isAuthenticated } = useUserSession();
  const { getWorkspaceInfo } = useWorkspace();

  const { setIsApolloClientReady } = useApolloClientStore();

  const workspaceInfo = getWorkspaceInfo();

  const [apolloClient, setApolloClient] = useState<ApolloClient<NormalizedCacheObject>>(
    generateApolloClientInstance(getIdToken, redirectToLogin),
  );

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    let cancelled = false;
    let phoenixSocket: ReturnType<typeof generatePhoenixSocket> | undefined;

    const idTokenRefreshInterval = setInterval(() => {
      getIdToken();
    }, ID_TOKEN_REFRESH_INTERVAL_MS);

    getIdToken().then(() => {
      if (cancelled) {
        return;
      }

      phoenixSocket = generatePhoenixSocket(getCachedIdToken);

      phoenixSocket.onOpen(() => {
        if (cancelled) {
          return;
        }

        getIdToken();
      });

      phoenixSocket.onClose(() => {
        if (cancelled) {
          return;
        }

        getIdToken();
      });

      const newClient = generateApolloClientInstanceWithPhoenixSocket(getIdToken, redirectToLogin, phoenixSocket);
      setApolloClient(newClient);

      setTimeout(() => {
        setIsApolloClientReady(true);
      }, 250);
    });

    return () => {
      cancelled = true;
      clearInterval(idTokenRefreshInterval);
      phoenixSocket?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceInfo?.clientId, workspaceInfo?.accessGroupSlug, isAuthenticated]);

  return apolloClient;
};
